import { dollarsCompact } from "../lib/format";
import type { LiveConfig } from "../lib/types";

type Props = {
  cfg: LiveConfig | null;
};

/**
 * Top-of-page banner showing the bot's live-trading safety state.
 *
 * Four visual states (most-defensive first):
 *
 * 1. **`kill_switch_active=true`** — full-width RED banner. The bot is
 *    refusing ALL new orders (paper or live). Operator action required.
 * 2. **`live_allowed=true`** — full-width AMBER banner. Real-money
 *    trading is armed for at least one strategy. Surfaces the dollar
 *    cap and the approved strategies prominently.
 * 3. **`live_allowed=false` and we have data** — narrow GREEN strip:
 *    "paper trading only." Reassuring at-a-glance state.
 * 4. **No live_config in DB yet** — narrow GRAY strip: "live-config not
 *    yet pushed by the bot." Won't render the banner at all when
 *    there's nothing to say.
 *
 * The banner is fed by `/v1/status.live_config` which the bot refreshes
 * every meta-watchdog tick (hourly).
 */
export function LiveSafetyBanner({ cfg }: Props) {
  if (cfg == null) {
    // Pre-bot-upgrade or sync error — render a neutral hint.
    return (
      <div className="rounded-md border border-ink-700 bg-ink-900 px-3 py-2 text-xs text-slate-500">
        Live-safety telemetry not yet received from bot. Paper-trading state assumed.
      </div>
    );
  }

  if (cfg.kill_switch_active) {
    return (
      <div className="rounded-md border-2 border-rose-700 bg-rose-950/40 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden>
            🛑
          </span>
          <div className="flex-1">
            <div className="text-sm font-bold uppercase tracking-wider text-rose-300">
              Kill switch ACTIVE — bot is refusing all new orders
            </div>
            {cfg.kill_switch_reason && (
              <div className="mt-1 text-xs text-rose-200">
                {cfg.kill_switch_reason}
              </div>
            )}
            <div className="mt-1 font-mono text-[10px] text-rose-400">
              snapshot {cfg.as_of_utc}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cfg.live_allowed) {
    return (
      <div className="rounded-md border-2 border-amber-700 bg-amber-950/30 px-4 py-3">
        <div className="flex items-start gap-3">
          <span className="text-2xl" aria-hidden>
            💸
          </span>
          <div className="flex-1">
            <div className="text-sm font-bold uppercase tracking-wider text-amber-300">
              LIVE TRADING ARMED — real money in play
            </div>
            <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
              <div>
                <span className="text-amber-500">Strategies live:</span>{" "}
                <span className="font-mono text-amber-200">
                  {cfg.strategies_live_approved.length > 0
                    ? cfg.strategies_live_approved.join(", ")
                    : "none (LIVE_ALLOWED set but no LIVE.md approved)"}
                </span>
              </div>
              <div>
                <span className="text-amber-500">Per-trade cap:</span>{" "}
                <span className="font-mono text-amber-200">
                  {cfg.live_max_notional_cents != null
                    ? dollarsCompact(cfg.live_max_notional_cents)
                    : "UNSET (default-reject)"}
                </span>
              </div>
              <div>
                <span className="text-amber-500">Pending approvals:</span>{" "}
                <span className="font-mono text-amber-200">
                  {cfg.pending_trades_count}
                </span>
                {cfg.require_approval && (
                  <span className="ml-1 text-amber-400">(Discord)</span>
                )}
              </div>
            </div>
            <div className="mt-1 font-mono text-[10px] text-amber-500">
              snapshot {cfg.as_of_utc}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default — paper trading only. Compact reassuring strip.
  return (
    <div className="flex items-center justify-between rounded-md border border-emerald-900/50 bg-emerald-950/20 px-3 py-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-emerald-400">📄</span>
        <span className="text-emerald-300">
          Paper trading only — `LIVE_ALLOWED` sentinel not set.
        </span>
      </div>
      <div className="flex items-center gap-3 font-mono text-[10px] text-emerald-600">
        {cfg.pending_trades_count > 0 && (
          <span>queue: {cfg.pending_trades_count}</span>
        )}
        <span>{cfg.as_of_utc.slice(11, 16)}Z</span>
      </div>
    </div>
  );
}
